import { useEffect, useRef } from 'react';
import { useUser, setUser, usePermissions, setFeatureConfigs } from '@store';
import { usePollUserPermissionsQuery } from '../queries/pollUserPermissions.query.generated';
import { auth, buildGlobalUser, checkForPermittedRole } from '../services/authService';
import { reportProfileLoadFailure } from '../services/profileReport';
import { grantedFeatureConfigs, grantedFeatureNames } from '../services/featureGrants';
import { useRefreshUserClaims } from '../hooks/useRefreshUserClaims';

const POLL_INTERVAL_MS = 60_000;

const PermissionsPollingProvider = ({ children }: { children: React.ReactNode }) => {
  const user = useUser();
  const currentPermissions = usePermissions();
  const userId = user?.claims?.['x-hasura-user-id'];
  const initializedRef = useRef(false);
  // The polled role and grants whose refresh last came back without a profile. The stored
  // permissions do not change after such a failure, so every later poll would look like the
  // same change; this stops the claims write, the token refresh and the Sentry report from
  // repeating every minute until the polled data changes again.
  const failedRefreshRef = useRef<string | null>(null);
  const { refreshUserClaims } = useRefreshUserClaims();

  const { data } = usePollUserPermissionsQuery({
    variables: { userId: userId! },
    pollInterval: POLL_INTERVAL_MS,
    skip: !userId,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!data?.users_by_pk || !userId) return;

    const polledRole = data.users_by_pk.default_role;
    // A grant whose feature the role cannot select comes back with a null
    // feature, which means no access rather than a crash.
    const polledFeatures = grantedFeatureNames(data.users_by_pk.users_usertofeatures).sort();

    // Skip the first poll result — it matches current state
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }

    const currentRole = user?.profile?.default_role;
    const currentFeatures = [...currentPermissions].sort();

    const roleChanged = polledRole !== currentRole;
    const featuresChanged = JSON.stringify(polledFeatures) !== JSON.stringify(currentFeatures);

    // Update feature configs from poll data (has config field now)
    if (data.users_by_pk.users_usertofeatures) {
      setFeatureConfigs(grantedFeatureConfigs(data.users_by_pk.users_usertofeatures));
    }

    if (!roleChanged && !featuresChanged) return;

    const refreshKey = JSON.stringify([polledRole, polledFeatures]);
    if (failedRefreshRef.current === refreshKey) return;

    console.log('[PermissionsPolling] Change detected, refreshing permissions...');

    const refreshPermissions = async () => {
      try {
        // Bump updated_at to trigger backend Firebase claim regeneration
        if (userId) await refreshUserClaims(userId);
        await auth.currentUser?.getIdToken(true);
        const { user: globalUser, profileFailure, diagnostics } = await buildGlobalUser();
        const permitted = checkForPermittedRole(globalUser);
        // Never swap a loaded user for one without a profile: that is the "Unknown User" state.
        if (permitted && !globalUser.profile) {
          failedRefreshRef.current = refreshKey;
          reportProfileLoadFailure({
            phase: 'permissions_refresh',
            user: globalUser,
            failure: profileFailure,
            diagnostics,
            firebaseUid: auth.currentUser?.uid,
            attempts: 1,
            action: 'kept_existing_user',
          });
          return;
        }
        if (permitted) {
          failedRefreshRef.current = null;
          setUser(globalUser);
        }
      } catch (err) {
        console.error('[PermissionsPolling] Failed to refresh permissions', err);
      }
    };

    refreshPermissions();
  }, [data]);

  return <>{children}</>;
};

export default PermissionsPollingProvider;
